/**
 * GitlabConnectModal — self-serve GitLab setup to PARITY with GitHub.
 *
 * Shared between:
 *   - IntegrationsPage (the canonical Settings → Integrations page)
 *   - MarketplaceDetailModal (Connect button on a marketplace card whose
 *     workflow declares `gitlab` as a required integration).
 *
 * ONE INSTANCE PER MODAL: an account may hold up to two gitlab integrations —
 * a Cloud (gitlab.com) one and a Self-Hosted one. The Integrations page renders
 * them as two separate cards; each card opens this modal with a `mode` prop
 * ('cloud' | 'selfhosted' | undefined for the generic marketplace flow). The
 * modal scopes itself to THAT one instance (`myInstance`, matched out of GET
 * /gitlab/integrations by hostname): it shows the connect form until that
 * instance is connected, then its webhook + repo→agent setup + Disconnect.
 * There is no "add another instance" — each card owns exactly one.
 *
 * Sections (the modal grows as the user progresses), all so a tenant can wire
 * GitLab END-TO-END without anyone hand-writing DynamoDB rows / tokens:
 *
 *   1. CONNECT — instanceUrl + Personal Access Token, PLUS an optional
 *      `signingToken` (the GitLab-generated `whsec_…` per-webhook secret). The
 *      backend (handlers/gitlab.js connectGitlab) already accepts + stores it
 *      encrypted; the inbound webhook (gitlab-webhook.js) HMAC-validates each
 *      delivery against it. Optional — omit and the legacy static-token path
 *      still works.
 *
 *   2. WEBHOOK SETUP — surfaces the webhook URL + the secret the user must
 *      paste into GitLab (Project/Group → Settings → Webhooks), with the exact
 *      events to enable (Merge request + Comments). Mirrors how a GitHub App
 *      install surfaces its webhook config — here the user configures it by
 *      hand in their GitLab, so we SHOW the values + instructions.
 *
 *   3. REPO/GROUP → AGENT PICKER — the GitLab analog of the GitHub repo→agent
 *      link. The user picks a Zibby project, one of that project's deployed
 *      agents (workflows), and a GitLab project/group, then creates the link
 *      (POST /gitlab/links). That row is EXACTLY what gitlab-webhook.js
 *      resolveLink reads to route an inbound MR to the right review agent.
 *
 * The connect call + links CRUD live in hooks (useConnectGitlab /
 * useGitlabLinks / useCreateGitlabLink / useDeleteGitlabLink) so this component
 * stays presentation-focused. Reset transient state on isOpen transitions so
 * reopening always starts clean.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectGitlab } from '../../hooks/useIntegrations';
// Reuses the existing .gitlab-*-modal CSS shipped with IntegrationsPage —
// importing here so the styles travel with the component wherever it's
// rendered, instead of relying on the host page having loaded the sheet.
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function GitlabConnectModal({ isOpen, onClose, onConnected, mode }) {
  // The two Integrations-page cards open this SAME modal with different `mode`:
  //   - 'cloud'      → GitLab.com: URL LOCKED to https://gitlab.com, token-only.
  //   - 'selfhosted' → the user types their own server URL.
  //   - undefined    → generic (e.g. the marketplace "Connect GitLab" flow):
  //                    free-text URL (gitlab.com or self-hosted), neutral header,
  //                    matches whatever single instance is connected.
  const isCloud = mode === 'cloud';
  const isSelfHosted = mode === 'selfhosted';
  const CLOUD_URL = 'https://gitlab.com';
  // ── Connect form state ──────────────────────────────────────────────────
  const [instanceUrl, setInstanceUrl] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const connectMutation = useConnectGitlab();

  useEffect(() => {
    if (isOpen) {
      // Cloud mode pre-fills (and locks) the URL to gitlab.com; self-hosted /
      // generic start blank so the user types their server.
      setInstanceUrl(isCloud ? CLOUD_URL : '');
      setAccessToken('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen, isCloud]);

  const handleConnect = () => {
    if (!instanceUrl.trim() || !accessToken.trim()) {
      setError('GitLab URL and Personal Access Token are required.');
      return;
    }
    setConnecting(true);
    setError('');
    connectMutation.mutate(
      { instanceUrl: instanceUrl.trim(), accessToken: accessToken.trim() },
      {
        onSuccess: () => {
          setConnecting(false);
          setAccessToken('');
          onConnected?.();
          onClose?.(); // close — the Integrations card now shows "Connected".
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect GitLab. Please check your URL and token.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="gitlab-connect-modal"
    >
      {/* Stop click propagation at the modal-content level so a click inside
          doesn't bubble up to a parent (marketplace) modal backdrop. */}
      <div
        className="gitlab-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect GitLab{isCloud ? ' · Cloud (GitLab.com)' : isSelfHosted ? ' · Self-Hosted' : ''}</h2>

        {/* The modal does ONE thing: connect this instance (token, + URL for
            self-hosted). Webhook setup + repo→agent linking live in the Zibby
            project's settings, not here. Once connected, this closes and the
            Integrations card shows "Connected" (click it there to disconnect). */}
        <div>
            <p className="gitlab-modal-description">
              {isCloud ? (
                <>Paste a GitLab.com Personal Access Token with <code>read_api</code> and <code>read_repository</code> scopes. The instance is fixed to <code>https://gitlab.com</code> — no URL needed.</>
              ) : isSelfHosted ? (
                <>Enter your <strong>self-hosted</strong> GitLab server URL and a Personal Access Token with <code>read_api</code> and <code>read_repository</code> scopes.</>
              ) : (
                <>Enter a GitLab instance URL (<code>https://gitlab.com</code> or your self-hosted server) and a Personal Access Token with <code>read_api</code> and <code>read_repository</code> scopes.</>
              )}
            </p>

            {/* Self-hosted needs a server URL. Cloud is fixed to gitlab.com
                (set internally) so we don't render a useless locked field. */}
            {!isCloud && (
              <div className="gitlab-form-group">
                <label className="gitlab-form-label">GitLab Instance URL</label>
                <input
                  type="text"
                  value={instanceUrl}
                  placeholder="https://gitlab.company.com"
                  onChange={(e) => { setInstanceUrl(e.target.value || ''); setError(''); }}
                  className="gitlab-form-input"
                  disabled={connecting}
                />
              </div>
            )}

            <div className="gitlab-form-group">
              <label className="gitlab-form-label">Personal Access Token</label>
              <input
                type="password"
                value={accessToken}
                placeholder="glpat-xxxxxxxxxxxxxxxxxxxx"
                onChange={(e) => { setAccessToken(e.target.value || ''); setError(''); }}
                className="gitlab-form-input"
                disabled={connecting}
              />
            </div>

            {error && <p className="gitlab-modal-error">{error}</p>}

            <div className="gitlab-modal-buttons">
              <IonButton
                expand="block"
                onClick={handleConnect}
                disabled={connecting || !instanceUrl.trim() || !accessToken.trim()}
              >
                {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
              </IonButton>
              <IonButton
                expand="block"
                fill="clear"
                onClick={() => onClose?.()}
                disabled={connecting}
              >
                Cancel
              </IonButton>
            </div>
        </div>
      </div>
    </IonModal>
  );
}
